import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { getAllPosts, searchPosts } from '../components/blogUtils';
import { Link } from 'react-router-dom';
import { trackEvent } from '../utils/analytics';
import { ANALYTICS_EVENTS } from '../utils/analyticsEvents';
import { buildAppUrl } from '../utils/appLinks';
import { BLOG_FALLBACK_COVER, buildTitle, DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL } from '../utils/seo';

const POSTS_PER_PAGE = 6;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [activeTag, setActiveTag] = useState('All');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const focusTags = ['Integration', 'Life in Germany', 'Lifestyle & Living', 'Practical Tips'];
  const blogAppLink = buildAppUrl({ medium: 'blog_top', location: 'blog_top_banner' });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const tag = params.get('tag');
    if (tag) {
      setActiveTag(tag);
    }
  }, []);

  // Load posts and tags on component mount
  useEffect(() => {
    loadPostsAndTags();
  }, []);

  // Load posts and tags
  const loadPostsAndTags = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const postsData = await getAllPosts();
      
      setPosts(postsData);
      // tags are derived from posts
    } catch (err) {
      console.error('Error loading posts:', err);
      setError('Failed to load blog posts. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = async (searchQuery) => {
    setSearch(searchQuery);
    setPage(1);
    
    if (searchQuery.trim()) {
      try {
        setLoading(true);
        const searchResults = await searchPosts(searchQuery);
        setPosts(searchResults);
      } catch (err) {
        console.error('Error searching posts:', err);
        setError('Search failed. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      // If search is empty, reload all posts
      loadPostsAndTags();
    }
  };

  // Filter posts by tag
  const filteredPosts = posts.filter(post => {
    const matchesTag =
      activeTag === 'All' || (post.tags && post.tags.includes(activeTag));
    return matchesTag;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE) || 1;
  const paginatedPosts = filteredPosts.slice(
    (page - 1) * POSTS_PER_PAGE,
    page * POSTS_PER_PAGE
  );

  const showFeatured = page === 1 && !search.trim() && activeTag === 'All' && paginatedPosts.length > 0;
  const featuredPost = showFeatured ? paginatedPosts[0] : null;
  const remainingPosts = showFeatured ? paginatedPosts.slice(1) : paginatedPosts;

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [activeTag]);

  if (loading && posts.length === 0) {
    return (
      <main
        className="max-w-5xl mx-auto px-6 py-16"
      >
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.35em] text-[#7b7671]">Journal</p>
          <h1 className="font-display text-4xl md:text-5xl mt-4 text-[#1c1b1a]">Latest writing</h1>
        </div>
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-stone-300 border-t-[#7b7671]"></div>
          <span className="ml-3 text-lg text-[#6a6661]">Loading posts...</span>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main
        className="max-w-5xl mx-auto px-6 py-16"
      >
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.35em] text-[#7b7671]">Journal</p>
          <h1 className="font-display text-4xl md:text-5xl mt-4 text-[#1c1b1a]">Latest writing</h1>
        </div>
        <div className="text-center py-16">
          <p className="text-[#b4533c] mb-4">{error}</p>
          <button
            onClick={loadPostsAndTags}
            className="bg-[#2d2b29] text-[#f5f4f2] px-6 py-2 uppercase tracking-[0.25em] text-xs hover:bg-[#1f1d1b] transition-colors"
          >
            Try Again
          </button>
        </div>
      </main>
    );
  }

  const getReadingTime = (content) => {
    if (!content) return null;
    const words = content.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length;
    const mins = Math.max(1, Math.ceil(words / 200));
    return `${mins} min read`;
  };

  const allTags = ['All', ...focusTags];

  return (
    <>
      <Helmet>
        <title>{buildTitle('Integration, Health & Life in Germany — Blog')}</title>
        <meta name="description" content="Practical guides on German integration, health, vegan African recipes, and diaspora life — written by Sista Kyerewaa, community educator and registered nurse." />
        <meta property="og:title" content={buildTitle('Integration, Health & Life in Germany — Blog')} />
        <meta property="og:description" content="Practical guides on German integration, health, vegan African recipes, and diaspora life for African families." />
        <meta property="og:type" content="blog" />
        <meta property="og:url" content={`${SITE_URL}/blog`} />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={buildTitle('Integration, Health & Life in Germany — Blog')} />
        <meta property="twitter:description" content="Practical guides on German integration, health, and diaspora life for African families." />
        <meta property="twitter:image" content={DEFAULT_OG_IMAGE} />
        <link rel="canonical" href={`${SITE_URL}/blog`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": `${SITE_NAME} — Integration & Life in Germany Blog`,
            "description": "Practical guides on integration, health, and diaspora life for African families in Germany.",
            "url": `${SITE_URL}/blog`,
            "author": {
              "@type": "Person",
              "name": "Sista Kyerewaa",
              "url": `${SITE_URL}/about`,
              "sameAs": ["https://www.tiktok.com/@sistakyerewaa"]
            },
            "publisher": {
              "@type": "Organization",
              "name": SITE_NAME,
              "logo": { "@type": "ImageObject", "url": `${SITE_URL}/images/sistakyerewaa-logo.svg` }
            },
            "blogPost": [...(featuredPost ? [featuredPost] : []), ...remainingPosts].slice(0, 6).map((post) => ({
              "@type": "BlogPosting",
              "headline": post.title,
              "description": post.excerpt || post.title,
              "datePublished": post.date,
              "dateModified": post.modified || post.date,
              "author": {
                "@type": "Person",
                "name": "Sista Kyerewaa",
                "url": `${SITE_URL}/about`,
              },
              "url": `${SITE_URL}/blog/${post.slug}`,
              "image": post.cover || DEFAULT_OG_IMAGE,
            })),
          })}
        </script>
      </Helmet>

      <main className="max-w-6xl mx-auto px-6 py-16">

        {/* Page header */}
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.35em] text-[#CC5833]">Blog</p>
          <h1 className="font-display text-4xl md:text-5xl mt-3 text-[#1c1b1a]">
            Integration, health &amp; life in Germany
          </h1>
          <p className="text-[#6a6661] mt-4 max-w-2xl">
            Practical guides and honest reflections for African families navigating life in Germany — written by a registered nurse and community educator.
          </p>
        </div>

        {/* Single filter bar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-10">
          <div className="flex flex-wrap gap-2 flex-1">
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => setActiveTag(tag)}
                className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors min-h-[44px] ${
                  activeTag === tag
                    ? 'bg-[#2d2b29] text-[#f5f4f2] border-[#2d2b29]'
                    : 'bg-transparent text-[#6b6660] border-stone-200 hover:text-[#2d2b29]'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => handleSearch(e.target.value)}
              className="w-full sm:w-52 px-4 py-2 border border-stone-200 bg-white/70 focus:outline-none focus:ring-2 focus:ring-[#c9c5bf] text-sm"
            />
            {(search || activeTag !== 'All') && (
              <button
                onClick={() => { setSearch(''); setActiveTag('All'); loadPostsAndTags(); }}
                className="px-3 py-2 border border-stone-200 text-xs uppercase tracking-[0.2em] text-[#6b6660] hover:text-[#2d2b29] whitespace-nowrap"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-stone-300 border-t-[#7b7671]" />
            <span className="ml-3 text-[#6a6661]">Loading posts…</span>
          </div>
        )}

        {/* Featured post */}
        {!loading && featuredPost && (
          <Link
            to={`/blog/${featuredPost.slug}`}
            className="block border border-stone-200 bg-white/80 mb-10 group"
          >
            <img
              src={featuredPost.cover || BLOG_FALLBACK_COVER}
              alt={`Cover for ${featuredPost.title}`}
              className="w-full h-72 md:h-80 object-cover"
              fetchPriority="high"
              onError={e => { e.currentTarget.src = BLOG_FALLBACK_COVER; }}
            />
            <div className="p-8">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className="text-xs uppercase tracking-[0.2em] text-[#CC5833]">Latest</span>
                <span className="text-xs uppercase tracking-[0.2em] text-[#7b7671]">
                  {new Date(featuredPost.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
                {getReadingTime(featuredPost.content) && (
                  <span className="text-xs uppercase tracking-[0.2em] text-[#7b7671]">
                    {getReadingTime(featuredPost.content)}
                  </span>
                )}
              </div>
              <h2 className="font-display text-3xl md:text-4xl text-[#2d2b29] mb-3 group-hover:text-[#1f1d1b] transition-colors">
                {featuredPost.title}
              </h2>
              <p className="text-[#6a6661] max-w-3xl">{featuredPost.excerpt}</p>
              <p className="mt-4 text-xs uppercase tracking-[0.25em] text-[#2d2b29]">Read the guide →</p>
            </div>
          </Link>
        )}

        {/* Posts grid + sidebar */}
        <div className="grid gap-10 md:grid-cols-[2fr_1fr]">
          {!loading && remainingPosts.length > 0 ? (
            <>
              <div className="grid gap-8">
                {remainingPosts.map(post => (
                  <Link
                    to={`/blog/${post.slug}`}
                    key={post.slug}
                    className="block border border-stone-200 bg-white/70 hover:shadow-md transition-shadow duration-200 group"
                  >
                    <img
                      src={post.cover || BLOG_FALLBACK_COVER}
                      alt={`Cover for ${post.title}`}
                      className="w-full h-48 object-cover"
                      loading="lazy"
                      onError={e => { e.currentTarget.src = BLOG_FALLBACK_COVER; }}
                    />
                    <div className="p-6">
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <span className="text-xs uppercase tracking-[0.2em] text-[#7b7671]">
                          {new Date(post.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        {getReadingTime(post.content) && (
                          <span className="text-xs uppercase tracking-[0.2em] text-[#7b7671]">
                            · {getReadingTime(post.content)}
                          </span>
                        )}
                      </div>
                      <h2 className="font-display text-2xl text-[#2d2b29] mb-2 group-hover:text-[#1f1d1b] transition-colors">
                        {post.title}
                      </h2>
                      <p className="text-[#6a6661] mb-4 line-clamp-3">{post.excerpt}</p>
                      <div className="flex flex-wrap gap-2">
                        {post.tags && post.tags.slice(0, 2).map(tag => (
                          <span key={tag} className="px-2 py-1 text-[0.65rem] uppercase tracking-[0.2em] bg-[#e7e4df] text-[#2d2b29]">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Sidebar */}
              <aside className="space-y-6">
                {/* Checklist CTA */}
                <div className="border border-stone-200 bg-white/70 p-6">
                  <p className="text-xs uppercase tracking-[0.25em] text-[#CC5833]">Free resource</p>
                  <h3 className="font-display text-xl text-[#2d2b29] mt-2">Germany Starter Checklist</h3>
                  <p className="text-sm text-[#6a6661] mt-2">
                    Your first steps — Anmeldung, Krankenkasse, tax ID and more — in one clear list.
                  </p>
                  <Link
                    to="/checklist"
                    className="inline-flex items-center justify-center mt-4 w-full px-4 py-3 bg-[#2E4036] text-[#F2F0E9] text-xs uppercase tracking-[0.25em] hover:bg-[#1a2a20] transition-colors"
                  >
                    Get it free
                  </Link>
                </div>

                {/* App CTA */}
                <div className="border border-stone-200 bg-[#F2F0E9] p-6">
                  <p className="text-xs uppercase tracking-[0.25em] text-[#CC5833]">Language app</p>
                  <h3 className="font-display text-xl text-[#2d2b29] mt-2">Learn German for real life</h3>
                  <p className="text-sm text-[#6a6661] mt-2">
                    Practice German for integration — Behörden, health, daily life — with cultural context built in.
                  </p>
                  <a
                    href={blogAppLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent(ANALYTICS_EVENTS.APP_OPEN_CLICK, { location: 'blog_sidebar', outbound: true })}
                    className="inline-flex items-center justify-center mt-4 w-full px-4 py-3 border border-[#8C7B6B]/40 text-xs uppercase tracking-[0.25em] text-[#2d2b29] hover:bg-[#e7e4df] transition-colors"
                  >
                    Try the app
                  </a>
                </div>

                {/* Topic filter in sidebar */}
                <div className="border border-stone-200 bg-white/70 p-6">
                  <p className="text-xs uppercase tracking-[0.25em] text-[#7b7671] mb-3">Browse by topic</p>
                  <div className="flex flex-col gap-1">
                    {allTags.map(tag => (
                      <button
                        key={tag}
                        onClick={() => { setActiveTag(tag); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        className={`text-left text-sm py-1.5 border-l-2 pl-3 transition-colors ${
                          activeTag === tag
                            ? 'border-[#CC5833] text-[#2d2b29] font-medium'
                            : 'border-transparent text-[#6a6661] hover:text-[#2d2b29] hover:border-[#D4CFC6]'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              </aside>
            </>
          ) : !loading ? (
            <div className="text-center text-[#6a6661] py-16 md:col-span-2">
              <p className="text-lg">No posts found. Try a different search or topic.</p>
              <button
                onClick={() => { setSearch(''); setActiveTag('All'); loadPostsAndTags(); }}
                className="mt-4 text-sm uppercase tracking-[0.25em] text-[#2d2b29] underline"
              >
                Clear filters
              </button>
            </div>
          ) : null}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-12 text-xs uppercase tracking-[0.25em] text-[#6b6660]">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-5 py-3 border border-stone-200 hover:text-[#2d2b29] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← Previous
            </button>
            <span>Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-5 py-3 border border-stone-200 hover:text-[#2d2b29] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next →
            </button>
          </div>
        )}
      </main>
    </>
  );
}
